import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Search, UserPlus, QrCode, User, Tag, Gift, X, Ticket, ShoppingCart, ChevronRight, ChefHat } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { staffApi } from '../../services/api';
import QrScanner from '../components/QrScanner';
import OfflineBanner from '../../components/OfflineBanner';
import BugReportButton from '../../components/BugReportButton';

export default function StaffHome() {
  const { staffUser, staffLogout } = useAuth();
  const { logo_url, primary_color, secondary_color, business_name, tier_names } = useBranding();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState({ customers_served: 0, points_assigned: 0, rewards_redeemed: 0 });
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const inputRef = useRef(null);
  const suggestTimer = useRef(null);

  useEffect(() => {
    staffApi.get('/staff/today-stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (query.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await staffApi.get(`/customers/suggest?q=${encodeURIComponent(query)}`);
        const list = res.data.customers || [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(suggestTimer.current);
  }, [query]);

  const handleSearch = useCallback(async () => {
    if (query.length < 2) { toast.error('Inserisci almeno 2 caratteri'); return; }
    setSearching(true);
    setShowSuggestions(false);
    try {
      if (/^\d+$/.test(query) && query.length >= 9) {
        const res = await staffApi.get(`/customers/search?phone=${query}`);
        if (res.data && res.data.id) {
          navigate(`/staff/customer/${res.data.id}`);
          return;
        }
      }
      const res = await staffApi.get(`/customers/suggest?q=${encodeURIComponent(query)}`);
      const list = res.data.customers || [];
      if (list.length === 1) {
        navigate(`/staff/customer/${list[0].id}`);
      } else if (list.length > 1) {
        setSuggestions(list);
        setShowSuggestions(true);
        toast.info(`${list.length} risultati trovati`);
      } else {
        toast.info('Nessun cliente trovato');
      }
    } catch { toast.error('Errore nella ricerca'); }
    finally { setSearching(false); }
  }, [query, navigate]);

  const selectSuggestion = (customer) => {
    setShowSuggestions(false);
    setQuery('');
    navigate(`/staff/customer/${customer.id}`);
  };

  const handleLogout = () => { staffLogout(); navigate('/staff/login'); };
  const handleQrScan = (customerId) => { setShowQrScanner(false); navigate(`/staff/customer/${customerId}`); };
  const handleTicketScan = async (qrCode) => {
    setShowQrScanner(false);
    try {
      const res = await staffApi.post('/events/tickets/checkin', { qr_code: qrCode });
      if (res.data.status === 'success') {
        toast.success(res.data.message);
      } else if (res.data.status === 'already_checked_in') {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Biglietto non valido');
    }
  };

  const quickActions = [
    { label: 'Scansiona QR', icon: QrCode, borderColor: `${primary_color}40`, bgColor: `${primary_color}10`, textColor: primary_color, action: () => setShowQrScanner(true), testid: 'quick-scan-qr' },
    { label: 'Nuovo Cliente', icon: UserPlus, borderColor: `${secondary_color}40`, bgColor: `${secondary_color}10`, textColor: secondary_color, action: () => navigate('/staff/register'), testid: 'quick-new-customer' },
    { label: 'Check-in Eventi', icon: Ticket, borderColor: `${primary_color}40`, bgColor: `${primary_color}10`, textColor: primary_color, action: () => navigate('/staff/tickets'), testid: 'quick-ticket-checkin' },
    { label: 'Cucina', icon: ChefHat, borderColor: '#F9731640', bgColor: '#F9731610', textColor: '#F97316', action: () => navigate('/staff/kitchen'), testid: 'quick-kitchen' },
  ];

  return (
    <div className="staff-app flex flex-col min-h-dvh">
      <OfflineBanner token={staffUser?.token} />

      {/* Header — staff is using the Lazo platform; brand is context */}
      <div className="px-5 pt-6 pb-4 flex items-center justify-between animate-fade-in-up">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 bg-[#D44E20] flex items-center justify-center">
            <span className="text-white font-black text-lg tracking-tight">L</span>
          </div>
          <div>
            <p className="text-[#888888] text-xs">
              Lazo{business_name && <span className="ml-1">· {business_name}</span>}
            </p>
            <h1 className="text-white font-bold text-xl leading-tight" data-testid="staff-name">{staffUser?.name || 'Operatore'}</h1>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="staff-logout-btn" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-[#888] hover:text-white hover:bg-white/10 transition-all border border-white/5">
          <LogOut size={18} />
        </button>
      </div>

      {/* Stats row */}
      <div className="px-5 mb-5 animate-fade-in-up stagger-1">
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Serviti oggi', value: stats.customers_served, testid: 'stat-customers' },
            { label: 'Punti dati', value: stats.points_assigned, testid: 'stat-points' },
            { label: 'Premi riscattati', value: stats.rewards_redeemed, testid: 'stat-rewards' },
          ].map((s, i) => (
            <div key={s.label} className={`bg-[#18181E] rounded-2xl p-3 text-center border border-white/5 animate-fade-in-up stagger-${i + 2}`} data-testid={s.testid}>
              <div className="text-white font-bold text-2xl tabular-nums">{s.value}</div>
              <div className="text-[#888888] text-[10px] mt-0.5 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search section */}
      <div className="px-5 mb-5 flex-1 animate-fade-in-up stagger-3 relative z-30">
        <div className="bg-[#18181E] rounded-2xl p-5 border border-white/5">
          <p className="text-[#888888] text-sm font-medium mb-3">Cerca cliente</p>

          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder="Numero di telefono o nome..."
              data-testid="search-input"
              className="w-full h-14 bg-[#222228] text-white text-lg rounded-2xl pl-12 pr-12 border border-white/10 focus:outline-none placeholder:text-[#444] transition-all"
              style={{ borderColor: query ? primary_color : undefined }}
              autoComplete="off"
            />
            <Search size={18} className="absolute left-4 top-[18px] text-[#555]" />
            {query && (
              <button
                onClick={() => { setQuery(''); setSuggestions([]); setShowSuggestions(false); }}
                className="absolute right-3 top-[14px] w-7 h-7 flex items-center justify-center rounded-full bg-white/10 text-[#888] hover:text-white transition-colors"
              >
                <X size={14} />
              </button>
            )}

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#1E1E26] border border-white/10 rounded-2xl overflow-hidden z-30 shadow-2xl max-h-[60vh] overflow-y-auto" data-testid="suggestions-dropdown">
                {suggestions.map((c, idx) => (
                  <button
                    key={c.id}
                    onClick={() => selectSuggestion(c)}
                    data-testid={`suggestion-${c.id}`}
                    className={`w-full flex flex-col px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-white/5 last:border-0 animate-fade-in-up stagger-${idx + 1}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primary_color}20` }}>
                        <User size={14} style={{ color: primary_color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{c.name}</p>
                        <p className="text-[#888] text-xs">{c.phone}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="text-sm font-bold tabular-nums" style={{ color: primary_color }}>{c.points} pt</span>
                        <p className="text-[#555] text-[10px] uppercase">{tier_names?.[c.tier] || c.tier}</p>
                      </div>
                    </div>
                    {((c.tier_benefits && c.tier_benefits.length > 0) || (c.redeemable_rewards && c.redeemable_rewards.length > 0)) && (
                      <div className="flex flex-wrap gap-1.5 mt-2 ml-12" data-testid={`badges-${c.id}`}>
                        {c.tier_benefits?.map((b, i) => (
                          <span key={`tb-${i}`} data-testid={`badge-benefit-${i}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              b.type === 'discount'
                                ? 'bg-[#3ECF8E]/15 text-[#3ECF8E] border border-[#3ECF8E]/20'
                                : b.claimed < b.max
                                  ? 'bg-[#FFAA00]/15 text-[#FFAA00] border border-[#FFAA00]/20'
                                  : 'bg-[#555]/20 text-[#666] border border-[#555]/20 line-through'
                            }`}>
                            {b.type === 'discount' ? <Tag size={8} /> : <Gift size={8} />}
                            {b.label}
                          </span>
                        ))}
                        {c.redeemable_rewards?.slice(0, 2).map((r, i) => (
                          <span key={`rw-${i}`} data-testid={`badge-reward-${i}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ backgroundColor: `${primary_color}15`, color: primary_color, border: `1px solid ${primary_color}20` }}>
                            {r.emoji} {r.name}
                          </span>
                        ))}
                        {c.redeemable_rewards?.length > 2 && (
                          <span className="text-[10px] text-[#888]">+{c.redeemable_rewards.length - 2} premi</span>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search button */}
          <button
            onClick={handleSearch}
            disabled={query.length < 2 || searching}
            data-testid="search-btn"
            className="mt-4 w-full h-12 text-white font-bold text-base rounded-[14px] disabled:opacity-40 active:scale-95 transition-all flex items-center justify-center gap-2"
            style={{ backgroundColor: primary_color }}
          >
            {searching ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : <Search size={18} />}
            {searching ? 'Ricerca...' : 'Cerca'}
          </button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="px-5 pb-4 animate-fade-in-up stagger-4">
        <p className="text-[#888888] text-sm font-medium mb-3">Azioni rapide</p>
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map(a => (
            <button
              key={a.label}
              onClick={a.action}
              data-testid={a.testid}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl border bg-[#18181E] active:scale-95 transition-all"
              style={{ borderColor: a.borderColor, backgroundColor: a.bgColor }}
            >
              <a.icon size={22} style={{ color: a.textColor }} />
              <span className="text-[11px] font-semibold" style={{ color: a.textColor }}>{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Eventi section */}
      <div className="px-5 pb-8 animate-fade-in-up stagger-5">
        <p className="text-[#888888] text-sm font-medium mb-3">Eventi</p>
        <button
          onClick={() => navigate('/staff/events')}
          data-testid="staff-section-events"
          className="w-full bg-[#18181E] rounded-2xl p-4 border border-white/5 active:scale-[0.98] transition-all flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primary_color}20` }}>
            <ShoppingCart size={18} style={{ color: primary_color }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm">Vendita biglietti & disponibilità</p>
            <p className="text-[#888] text-xs">Vendi alla cassa e controlla i posti rimasti</p>
          </div>
          <ChevronRight size={16} className="text-[#666] flex-shrink-0" />
        </button>
      </div>

      {showQrScanner && (
        <QrScanner onScan={handleQrScan} onTicketScan={handleTicketScan} onClose={() => setShowQrScanner(false)} />
      )}
      <BugReportButton userRole="staff" userName={staffUser?.name || ''} brandName="" />
    </div>
  );
}
